import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Role } from '@nexa/auth';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  ShieldCheck,
  Ticket,
  UserCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RoleGate } from '@/auth/role-gate';
import { cn } from '@/lib/cn';
import {
  useContainDisruption,
  useForceAcceptAllDisruption,
  useIssueDisruptionTickets,
} from '@/lib/disruptions/use-disruptions';
import { ContainRefusedError } from '@/lib/disruptions/api';
import type {
  ResolutionOp,
  ResolutionState,
  ResolutionStep,
  ResolutionStepStatus,
} from '@/lib/disruptions/types';
import {
  STATUS_CHIP,
  STEP_HINT_KEY,
  rebookingPolicyGate,
  skippedChip,
  skippedIsPositive,
  stepCounter,
  stepIcon,
  stepState,
  stepSubject,
} from './resolution-steps';
import { RebookingPolicyNotice } from './rebooking-policy-notice';
import { RosterBulkBar } from './roster-bulk-bar';
import { EventAllocationActionBar } from './event-allocation-action-bar';
import { EventVoucherActionBar } from './event-voucher-action-bar';
import { AutomaticAcceptancePanel } from './automatic-acceptance-panel';

// disruption-overview — the guided RESOLUTION STEPPER (6 steps, operator order):
//   rebook → issue tickets → book hotels (HOLD) → vouchers → offer/accept → contain.
// Every row reads the B1 `resolution-state.steps[]` (counters from cases + booking
// facts, never from a run marker alone) and composes the EXISTING disruption-scoped
// action bars — it does not re-implement them. Only the recommended step (or a
// running one) is EXPANDED with its action; every other row is one compact line, so
// the whole overview fits ONE viewport (no page scroll — the list scrolls inside).

interface ResolutionStepperProps {
  disruptionUrn: string;
  state: ResolutionState;
}

export function StatusChip({
  status,
  op,
  label,
  policyDisabled,
}: {
  status: ResolutionStepStatus;
  op?: ResolutionOp;
  /** State label to show instead of the generic status word ("Booked · unconfirmed"). */
  label?: string;
  /** rebooking only — a skip the station's policy caused reads as its own fact. */
  policyDisabled?: boolean | undefined;
}) {
  const { t } = useTranslation('rebooking');
  const base = STATUS_CHIP[status] ?? STATUS_CHIP.UNKNOWN;
  const override = op ? skippedChip({ op, status, policyDisabled }, t) : undefined;
  const Icon = base.icon;
  // The explicit state label wins over the per-op skip relabel, which wins over
  // the generic status word; the tone follows the relabel.
  const text = label ?? override?.label ?? t(base.labelKey);
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
        override?.className ?? base.className,
      )}
      data-testid={`resolution-step-status-${status}`}
    >
      <Icon className={cn('h-3 w-3', status === 'RUNNING' && 'animate-spin')} aria-hidden />
      {text}
    </span>
  );
}

export function ResolutionStepper({ disruptionUrn, state }: ResolutionStepperProps) {
  const [openOp, setOpenOp] = useState<ResolutionOp | null>(null);
  const recommendedOp = state.nextStep.op;
  return (
    <ol className="flex min-h-0 flex-col gap-1.5" data-testid="resolution-stepper">
      {state.steps.map((step, i) => {
        const recommended = recommendedOp === step.op;
        const expanded = recommended || step.status === 'RUNNING' || openOp === step.op;
        return (
          <StepRow
            key={step.op}
            index={i + 1}
            disruptionUrn={disruptionUrn}
            step={step}
            state={state}
            recommended={recommended}
            expanded={expanded}
            onToggle={() => setOpenOp((cur) => (cur === step.op ? null : step.op))}
          />
        );
      })}
    </ol>
  );
}

function StepRow({
  index,
  disruptionUrn,
  step,
  state,
  recommended,
  expanded,
  onToggle,
}: {
  index: number;
  disruptionUrn: string;
  step: ResolutionStep;
  state: ResolutionState;
  recommended: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('rebooking');
  const Icon = stepIcon(step.op);
  // Subject + STATE, never the action verb: "Hotels · Booked · unconfirmed".
  const st = stepState(step, state.kpis.groups ?? null, state.steps);
  const reason =
    recommended && state.nextStep.op !== 'none'
      ? state.nextStep.reason || st.detail || t(STEP_HINT_KEY[step.op])
      : st.detail || step.reason || t(STEP_HINT_KEY[step.op]);
  return (
    <li
      className={cn(
        'rounded-lg border bg-card transition-colors',
        recommended ? 'border-primary ring-1 ring-primary/40' : 'border-border',
        step.status === 'SKIPPED' && !skippedIsPositive(step) && 'opacity-60',
      )}
      data-testid={`resolution-step-${step.op}`}
      data-recommended={recommended ? 'true' : undefined}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2.5 px-3 py-2 text-left"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span
          className={cn(
            'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
            step.status === 'DONE'
              ? 'bg-emerald-500 text-white'
              : recommended
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground',
          )}
          aria-hidden
        >
          {step.status === 'DONE' ? <CheckCircle2 className="h-3.5 w-3.5" /> : index}
        </span>
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="shrink-0 text-sm font-semibold text-foreground">{stepSubject(step.op)}</span>
        <StatusChip
          status={step.status}
          op={step.op}
          label={st.label}
          policyDisabled={step.policyDisabled}
        />
        <span
          className="shrink-0 text-xs tabular-nums text-muted-foreground"
          data-testid={`resolution-step-progress-${step.op}`}
        >
          {stepCounter(step)}
        </span>
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground" title={reason}>
          {reason}
        </span>
        {recommended ? (
          <span
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary"
            data-testid="resolution-next-step"
          >
            <ChevronRight className="h-3 w-3" aria-hidden />
            {t('stepper.next')}
          </span>
        ) : null}
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', expanded && 'rotate-180')}
          aria-hidden
        />
      </button>
      {expanded ? (
        <div className="border-t border-border px-3 py-2">
          <StepAction disruptionUrn={disruptionUrn} op={step.op} step={step} state={state} />
        </div>
      ) : null}
    </li>
  );
}

/**
 * The action region for a step. Composes the EXISTING bars (never re-implements
 * them); tickets/acceptance are the disruption-wide fan-outs (same endpoints the
 * one-click fires), contain is supervisor-gated.
 */
function StepAction({
  disruptionUrn,
  op,
  step,
  state,
}: {
  disruptionUrn: string;
  op: ResolutionOp;
  step: ResolutionStep;
  state: ResolutionState;
}) {
  const { t } = useTranslation('rebooking');
  switch (op) {
    case 'rebooking': {
      // The station's policy has the engine off (rebooking TDS §18): no trigger at
      // all — neither the bulk bar nor the advanced (dry-run / accept-top-rank)
      // panel, since every one of them fires the same doomed run. Name the rule.
      const gate = rebookingPolicyGate(state.steps);
      if (gate.disabled) {
        return (
          <RebookingPolicyNotice
            policyUrn={gate.policyUrn}
            data-testid="resolution-rebooking-policy-gate"
          />
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <RosterBulkBar disruptionUrn={disruptionUrn} />
          <details className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
            <summary className="cursor-pointer select-none font-medium text-foreground">
              {t('stepper.advanced')}
            </summary>
            <div className="pt-2">
              <AutomaticAcceptancePanel disruptionUrn={disruptionUrn} />
            </div>
          </details>
        </div>
      );
    }
    case 'tickets':
      return <EventTicketsActionBar disruptionUrn={disruptionUrn} step={step} />;
    case 'hotels':
      return <EventAllocationActionBar disruptionUrn={disruptionUrn} />;
    case 'vouchers':
      return <EventVoucherActionBar disruptionUrn={disruptionUrn} />;
    case 'acceptance':
      return <EventAcceptanceActionBar disruptionUrn={disruptionUrn} step={step} state={state} />;
    case 'contain':
      return <ContainAction disruptionUrn={disruptionUrn} step={step} />;
    default:
      return null;
  }
}

/** Disruption-wide "Issue tickets (N)" — same fan-out the one-click fires. */
function EventTicketsActionBar({ disruptionUrn, step }: { disruptionUrn: string; step: ResolutionStep }) {
  const { t } = useTranslation('rebooking');
  const issue = useIssueDisruptionTickets(disruptionUrn);
  const n = step.remaining ?? step.total ?? 0;
  const busy = issue.isPending || step.status === 'RUNNING';
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm"
      data-testid="resolution-tickets-bar"
    >
      <span className="text-muted-foreground">
        {step.status === 'BLOCKED' ? step.reason : t('stepper.tickets.without', { count: n })}
      </span>
      {issue.data ? (
        <span className="text-xs text-emerald-700 dark:text-emerald-300" data-testid="resolution-tickets-tally">
          {t('stepper.tickets.tally', {
            issued: issue.data.tally.issued ?? 0,
            already: issue.data.tally.alreadyTicketed ?? 0,
          })}
          {(issue.data.tally.failed ?? 0) > 0
            ? ` · ${t('stepper.tickets.tallyFailed', { count: issue.data.tally.failed ?? 0 })}`
            : ''}
        </span>
      ) : null}
      {issue.isError ? (
        <span className="text-xs text-destructive">{t('stepper.tickets.error')}</span>
      ) : null}
      <div className="ml-auto">
        <Button
          size="sm"
          onClick={() => issue.mutate()}
          disabled={busy || step.status === 'BLOCKED' || step.status === 'SKIPPED' || n === 0}
          data-testid="resolution-tickets-issue"
        >
          {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : <Ticket className="mr-1 h-3 w-3" aria-hidden />}
          {busy ? t('stepper.tickets.issuing') : t('stepper.tickets.issue', { count: n })}
        </Button>
      </div>
    </div>
  );
}

/** Acceptance: everyone sees the tally; only a supervisor gets "Accept all (N)". */
function EventAcceptanceActionBar({
  disruptionUrn,
  step,
  state,
}: {
  disruptionUrn: string;
  step: ResolutionStep;
  state: ResolutionState;
}) {
  const { t } = useTranslation('rebooking');
  const accept = useForceAcceptAllDisruption(disruptionUrn);
  const g = state.kpis.groups;
  const awaiting = step.remaining ?? g?.awaiting ?? 0;
  const busy = accept.isPending || step.status === 'RUNNING';
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm"
      data-testid="resolution-acceptance-bar"
    >
      <span className="text-muted-foreground" data-testid="resolution-acceptance-tally">
        {g
          ? `${t('stepper.acceptance.tally', {
              accepted: g.accepted,
              atGate: g.atGate,
              awaiting: g.awaiting,
            })}${g.declined ? ` · ${t('stepper.acceptance.tallyDeclined', { count: g.declined })}` : ''}`
          : (step.reason ?? t(STEP_HINT_KEY.acceptance))}
      </span>
      {accept.data ? (
        <span className="text-xs text-emerald-700 dark:text-emerald-300">
          {t('stepper.acceptance.forced', { count: accept.data.tally.forced ?? 0 })}
        </span>
      ) : null}
      {accept.isError ? (
        <span className="text-xs text-destructive">{t('stepper.acceptance.error')}</span>
      ) : null}
      <div className="ml-auto flex items-center gap-2">
        <a href="#passengers" className="text-xs text-primary underline-offset-2 hover:underline">
          {t('stepper.acceptance.viewPerPnr')}
        </a>
        <RoleGate min={Role.OPS_SUPERVISOR}>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => accept.mutate()}
            disabled={busy || awaiting === 0}
            data-testid="resolution-acceptance-accept-all"
          >
            {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : <UserCheck className="mr-1 h-3 w-3" aria-hidden />}
            {busy ? t('stepper.acceptance.accepting') : t('stepper.acceptance.acceptAll', { count: awaiting })}
          </Button>
        </RoleGate>
      </div>
    </div>
  );
}

/**
 * The contain action — the final step. Supervisor-gated (matches the header's
 * "Mark contained"), reuses `useContainDisruption`. Blocked while work is
 * pending; "Contain anyway" stays as an explicit supervisor override.
 */
function ContainAction({ disruptionUrn, step }: { disruptionUrn: string; step: ResolutionStep }) {
  const { t } = useTranslation('rebooking');
  const contain = useContainDisruption(disruptionUrn);
  const [confirming, setConfirming] = useState(false);
  const mode = step.containMode;
  const done = step.status === 'DONE';
  // TRAVELLING = tickets issued, someone still to fly → NOT containable (no button).
  const waiting = step.status === 'BLOCKED' && mode === 'TRAVELLING';
  const label =
    mode === 'NATURAL' ? t('stepper.contain.confirmLabel') : t('stepper.contain.forceLabel');
  const refusal = contain.error instanceof ContainRefusedError ? contain.error.message : null;
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm"
      data-testid="resolution-contain-bar"
      data-contain-mode={mode}
    >
      <span className={cn('text-muted-foreground', waiting && 'text-amber-700 dark:text-amber-300')}>
        {done ? t('stepper.contain.done') : (step.reason ?? t(STEP_HINT_KEY.contain))}
      </span>
      {contain.isError ? (
        <span className="text-xs text-destructive" data-testid="resolution-contain-error">
          {refusal ?? t('stepper.contain.error')}
        </span>
      ) : null}
      {!done && !waiting ? (
        <div className="ml-auto flex items-center gap-2">
          {confirming ? (
            <>
              <span className="text-xs text-muted-foreground" data-testid="resolution-contain-confirm">
                {mode === 'NATURAL'
                  ? t('stepper.contain.confirmNatural')
                  : t('stepper.contain.confirmForced')}
              </span>
              <Button size="sm" variant="ghost" onClick={() => setConfirming(false)} data-testid="resolution-contain-cancel">
                {t('common:common.cancel')}
              </Button>
              <Button
                size="sm"
                variant={mode === 'NATURAL' ? 'default' : 'destructive'}
                onClick={() => {
                  setConfirming(false);
                  contain.mutate(undefined);
                }}
                disabled={contain.isPending}
                data-testid="resolution-contain"
              >
                {contain.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : <ShieldCheck className="mr-1 h-3 w-3" aria-hidden />}
                {label}
              </Button>
            </>
          ) : mode === 'NATURAL' ? (
            <Button size="sm" onClick={() => setConfirming(true)} disabled={contain.isPending} data-testid="resolution-contain-open">
              <ShieldCheck className="mr-1 h-3 w-3" aria-hidden />
              {label}
            </Button>
          ) : (
            <RoleGate min={Role.OPS_SUPERVISOR}>
              <Button size="sm" variant="outline" onClick={() => setConfirming(true)} disabled={contain.isPending} data-testid="resolution-contain-open">
                <ShieldCheck className="mr-1 h-3 w-3" aria-hidden />
                {label}
              </Button>
            </RoleGate>
          )}
        </div>
      ) : null}
    </div>
  );
}
